import React from 'react'

import {
  Box,
  Display,
  FlexDirection,
  JustifyContent,
  ResponsiveValue,
} from '@gousto-internal/citrus-react'
import type { RecipeGridContentBlock, StandardRecipeItem } from '@library/api-menu-experience'

import { RecipeCount } from '../CollectionScreen/RecipeCount'

export type SupplementaryItem = {
  id: string
  position: number | 'end'
  content: React.ReactNode
}

export type RecipeGridProps = {
  contentBlock: RecipeGridContentBlock
  recipeCountTextColor?: string
  supplementaryItems?: SupplementaryItem[]
  renderRecipeTile: (recipe: StandardRecipeItem, recipePosition: number) => React.ReactNode
}

const recipeGridItemWidth: ResponsiveValue<string> = [
  '100%',
  '100%',
  'calc((100% - 16px) / 2)',
  'calc((100% - 32px) / 3)',
]

const isStandardRecipeItem = (item: StandardRecipeItem): item is StandardRecipeItem =>
  item.type === 'standard-recipe'

type GridItemProps = {
  children: React.ReactNode
}

const GridItem = ({ children }: GridItemProps) => (
  <Box
    display={Display.Flex}
    flexBasis={recipeGridItemWidth}
    flexGrow={0}
    flexShrink={0}
    maxWidth={recipeGridItemWidth}
  >
    {children}
  </Box>
)

export const RecipeGrid = ({
  contentBlock,
  recipeCountTextColor,
  supplementaryItems = [],
  renderRecipeTile,
}: RecipeGridProps) => {
  const recipes = contentBlock.items.filter(isStandardRecipeItem)
  const numericSupplementaryItems = supplementaryItems.filter(
    (item): item is SupplementaryItem & { position: number } =>
      typeof item.position === 'number' && item.position > 0 && item.position < recipes.length,
  )
  const endSupplementaryItems = supplementaryItems.filter(({ position }) => position === 'end')

  const renderSupplementaryItems = (items: SupplementaryItem[]) =>
    items.map(({ id, content }) => <GridItem key={id}>{content}</GridItem>)

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={3}
      data-testid="recipe-grid"
    >
      {contentBlock.recipeCountLabel && (
        <Box paddingH={[3, 3, 0]}>
          <RecipeCount label={contentBlock.recipeCountLabel} textColor={recipeCountTextColor} />
        </Box>
      )}
      <Box
        display={Display.Flex}
        wrap
        justifyContent={JustifyContent.Center}
        gap={[3, 3, 4]}
        paddingH={[3, 3, 0]}
        data-testid="recipe-grid-items"
      >
        {recipes.map((item, index) => {
          const recipePosition = index + 1
          return (
            <React.Fragment key={item.id}>
              <GridItem>{renderRecipeTile(item, recipePosition)}</GridItem>
              {renderSupplementaryItems(
                numericSupplementaryItems.filter(({ position }) => position === recipePosition),
              )}
            </React.Fragment>
          )
        })}
        {renderSupplementaryItems(endSupplementaryItems)}
      </Box>
    </Box>
  )
}
